import { Link } from "@tanstack/react-router";
import { AlertTriangle, Bell, CheckCircle, CheckCircle2, ClipboardList, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useMarkAllNotificationsRead, useNotifications } from "@/hooks/use-notifications";
import { useMyTasks } from "@/hooks/use-tasks";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDueDate(dueDate: string | null): {
  label: string;
  tone: "overdue" | "soon" | "ok" | "none";
} {
  if (!dueDate) return { label: "No due date", tone: "none" };
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);
  const diffDays = Math.round((dueDay.getTime() - today.getTime()) / 86_400_000);
  const formatted = due.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (diffDays < 0) return { label: `Overdue · ${formatted}`, tone: "overdue" };
  if (diffDays === 0) return { label: `Due today`, tone: "soon" };
  if (diffDays <= 3) return { label: `Due ${formatted}`, tone: "soon" };
  return { label: `Due ${formatted}`, tone: "ok" };
}

function NotificationIcon({ type }: { type: string | null }) {
  if (type === "success") return <CheckCircle className="h-4 w-4 shrink-0 text-emerald-500" />;
  if (type === "warning") return <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />;
  if (type === "info") return <Info className="h-4 w-4 shrink-0 text-blue-500" />;
  return <Bell className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

function PriorityDot({ priority }: { priority: string | null }) {
  const color =
    priority === "high"
      ? "bg-red-500"
      : priority === "medium"
        ? "bg-amber-500"
        : priority === "low"
          ? "bg-slate-400"
          : "bg-slate-300";
  return <span className={cn("h-2 w-2 rounded-full", color)} aria-hidden />;
}

function StatusPill({ status }: { status: string | null }) {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    not_started: { bg: "bg-slate-100", text: "text-slate-700", label: "Not Started" },
    in_progress: { bg: "bg-blue-100", text: "text-blue-700", label: "In Progress" },
    in_review: { bg: "bg-violet-100", text: "text-violet-700", label: "In Review" },
    blocked: { bg: "bg-red-100", text: "text-red-700", label: "Blocked" },
    overdue: { bg: "bg-red-100", text: "text-red-700", label: "Overdue" },
  };
  const cfg = map[status ?? ""] ?? {
    bg: "bg-slate-100",
    text: "text-slate-700",
    label: status ?? "—",
  };
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium",
        cfg.bg,
        cfg.text,
      )}
    >
      {cfg.label}
    </span>
  );
}

export function ContractorDashboardPage() {
  const { data: session } = useSession();
  const role = session?.user?.role;
  const enabled = role === "contractor";

  const { data: notifications, isLoading: notifLoading } = useNotifications();
  const { data: tasks, isLoading: tasksLoading, isError: tasksError } = useMyTasks(enabled);
  const markAllRead = useMarkAllNotificationsRead();

  const firstName = (session?.user?.name ?? "").split(" ")[0] || "there";
  const unread = (notifications ?? []).filter((n) => !n.read);
  const visibleNotifications = unread.slice(0, 10);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h1 className="text-2xl font-semibold tracking-tight">Welcome, {firstName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your open notifications and the tasks assigned to you across all active projects.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Notifications panel */}
        <section className="rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold">
                Open Notifications
                {unread.length > 0 && (
                  <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                    {unread.length}
                  </span>
                )}
              </h2>
            </div>
            {unread.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
              >
                Mark all read
              </Button>
            )}
          </div>

          <div className="divide-y divide-border">
            {notifLoading && (
              <>
                <Skeleton className="m-4 h-12" />
                <Skeleton className="m-4 h-12" />
                <Skeleton className="m-4 h-12" />
              </>
            )}
            {!notifLoading && unread.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2 px-5 py-12 text-center">
                <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">You're all caught up.</p>
              </div>
            )}
            {visibleNotifications.map((n) => (
              <div key={n.id} className="flex items-start gap-3 px-5 py-3">
                <div className="mt-0.5">
                  <NotificationIcon type={n.type} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{n.title ?? "Notification"}</p>
                  {n.message && (
                    <p className="line-clamp-2 text-xs text-muted-foreground">{n.message}</p>
                  )}
                </div>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {relativeTime(n.date)}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* My tasks panel */}
        <section className="rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold">
                My Tasks
                {tasks && tasks.length > 0 && (
                  <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                    {tasks.length}
                  </span>
                )}
              </h2>
            </div>
          </div>

          <div className="divide-y divide-border">
            {tasksLoading && (
              <>
                <Skeleton className="m-4 h-12" />
                <Skeleton className="m-4 h-12" />
                <Skeleton className="m-4 h-12" />
              </>
            )}
            {tasksError && (
              <div className="px-5 py-12 text-center text-sm text-muted-foreground">
                Couldn't load your tasks. Try refreshing.
              </div>
            )}
            {!tasksLoading && !tasksError && tasks && tasks.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2 px-5 py-12 text-center">
                <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No tasks assigned to you.</p>
              </div>
            )}
            {tasks?.map((t) => {
              const due = formatDueDate(t.dueDate);
              return (
                <Link
                  key={t.id}
                  to="/projects/$projectId"
                  params={{ projectId: t.projectId }}
                  search={{ tab: "tasks" }}
                  className="flex items-start gap-3 px-5 py-3 transition-colors hover:bg-muted/50"
                >
                  <div className="mt-1.5">
                    <PriorityDot priority={t.priority} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{t.title ?? "Untitled task"}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                      {t.projectName && <span className="truncate">{t.projectName}</span>}
                      {t.projectName && <span className="opacity-40">·</span>}
                      <span
                        className={cn(
                          due.tone === "overdue" && "font-medium text-red-600",
                          due.tone === "soon" && "font-medium text-amber-600",
                        )}
                      >
                        {due.label}
                      </span>
                    </div>
                  </div>
                  <div className="shrink-0">
                    <StatusPill status={t.status} />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
